import React, { useState } from 'react';
import axios from '../axios-config';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Trash2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrivacyMyData() {
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [stage, setStage] = useState('init'); // init | confirm

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(`${API}/me/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `procureoneultra-mydata-${(data.data_subject?.email || 'user').replace(/[^a-z0-9]/gi, '_')}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Your data has been downloaded as JSON');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally { setExporting(false); }
  };

  const deleteAccount = async () => {
    if (confirmText !== 'DELETE_MY_ACCOUNT') { toast.error('Type DELETE_MY_ACCOUNT exactly to confirm'); return; }
    setDeleting(true);
    try {
      const { data } = await axios.delete(`${API}/me`, { data: { confirmation: confirmText, reason } });
      toast.success(`Account deleted (token: ${data.deletion_token})`);
      // Clear local auth and route home
      localStorage.removeItem('pou_access_token');
      setTimeout(() => navigate('/'), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Deletion failed');
    } finally { setDeleting(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-navy text-white">
        <nav className="container mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-white/80 hover:text-white inline-flex items-center text-sm" data-testid="privacy-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
          </button>
          <div className="font-bold inline-flex items-center"><ShieldCheck className="h-5 w-5 text-gold mr-1.5" /> Privacy & My Data</div>
          <div className="w-12" />
        </nav>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-2xl space-y-5">
        <div className="bg-card border border-border rounded-lg p-4 text-xs text-muted-foreground" data-testid="privacy-intro">
          <p>Under India's <strong>Digital Personal Data Protection Act, 2023 (DPDP)</strong>, you have the right to: (a) download a full copy of your personal data ProcureOne Ultra holds about you, and (b) request deletion of your account. Commercial records (tenders, bids, invoices) are retained as legally required but personal identifiers are anonymized.</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-5 space-y-3" data-testid="privacy-export-card">
          <h2 className="text-sm font-bold text-navy uppercase tracking-wide inline-flex items-center"><Download className="h-4 w-4 mr-1.5" /> Data Export</h2>
          <p className="text-xs text-muted-foreground">Downloads a JSON snapshot with: profile, subscriptions, tenders/bids, clarifications, success-fee invoices, legal acceptances, audit log of your actions, and founding offer (if applicable).</p>
          <button onClick={exportData} disabled={exporting} className="px-4 py-2.5 rounded bg-navy text-white text-sm font-bold disabled:opacity-50 inline-flex items-center" data-testid="privacy-export-btn">
            <Download className="h-4 w-4 mr-2" /> {exporting ? 'Exporting…' : 'Download my data (JSON)'}
          </button>
        </div>

        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-5 space-y-3" data-testid="privacy-delete-card">
          <h2 className="text-sm font-bold text-red-900 uppercase tracking-wide inline-flex items-center"><AlertTriangle className="h-4 w-4 mr-1.5" /> Delete Account</h2>
          <p className="text-xs text-red-900">Anonymizes your account: personal details (name, email, phone) are scrubbed. Commercial records you participated in (awarded tenders, paid invoices) are retained as legally required but with anonymized owner references. <strong>This action is irreversible.</strong></p>

          {stage === 'init' && (
            <button onClick={() => setStage('confirm')} className="px-4 py-2.5 rounded border-2 border-red-600 text-red-700 text-sm font-bold inline-flex items-center hover:bg-red-600 hover:text-white transition-colors" data-testid="privacy-delete-init-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Begin Account Deletion
            </button>
          )}

          {stage === 'confirm' && (
            <div className="space-y-3 bg-card border border-red-200 rounded p-4">
              <div>
                <label className="block text-xs font-bold text-navy mb-1">Reason for deletion (optional)</label>
                <input type="text" value={reason} onChange={e => setReason(e.target.value)} className="w-full px-3 py-2 rounded border border-input bg-background text-sm" placeholder="e.g., no longer needed" data-testid="privacy-delete-reason-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-navy mb-1">Type <code className="bg-red-100 text-red-700 px-1 rounded">DELETE_MY_ACCOUNT</code> to confirm</label>
                <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)} className="w-full px-3 py-2 rounded border border-red-300 bg-background text-sm font-mono" data-testid="privacy-delete-confirm-input" />
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setStage('init'); setConfirmText(''); setReason(''); }} className="flex-1 px-3 py-2 rounded border border-border text-sm font-bold" data-testid="privacy-delete-cancel-btn">Cancel</button>
                <button onClick={deleteAccount} disabled={deleting || confirmText !== 'DELETE_MY_ACCOUNT'} className="flex-1 px-3 py-2 rounded bg-red-600 text-white text-sm font-bold disabled:opacity-30 inline-flex items-center justify-center" data-testid="privacy-delete-confirm-btn">
                  <Trash2 className="h-4 w-4 mr-1.5" /> {deleting ? 'Deleting…' : 'Permanently delete'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
